// --- Capa editorial de artistas del servidor de la radio (listen.astra.fm) ---
// Contrato y algoritmo de slug definidos en ARTISTAS_WEB_INSTRUCCIONES.md.
//
// 🔴 **La ficha manda en foto y bio, dato a dato** (decisión de Pablo del
// 15.09.26, `DECISION_FICHA_ARTISTA_SIEMPRE_MANDA.md` en temp). Lo que le
// falte se busca fuera. Hasta ese día la ficha solo rellenaba, salvo con
// `priority: true`; ahora `priority` ya no decide el orden.

// Una petición por artista, no el catálogo entero. `editorial.json` pesa
// **1,9 MB con 2.633 artistas** —medido el 05.09.26— y la ficha por slug son
// unos 700 bytes. Con la revalidación en cada navegación que pide el §4, bajar
// el índice para leer un artista era traerse el listín para buscar un teléfono.
//
// ⚠️ **No se pudo usar hasta el 09.09.26.** El §1 recomendaba esta ruta desde el
// 02.09, pero su respuesta **no traía `priority`** —el campo del §2.1 que
// adelanta la bio curada a TheAudioDB y Last.fm—, ni el crédito de la imagen.
// Migrar entonces habría degradado en silencio a los 34 artistas priorizados.
// Avisado al servidor, corregido por ellos y comprobado aquí antes de cambiar:
// `propaganda`, `cranes` y `kadavar` responden con `priority: true` y su
// crédito.
import { LISTEN } from "./host";

const FICHA_URL = (slug) => `${LISTEN}/artists/${slug}.json`;
const INDICE_URL = `${LISTEN}/artists/index.json`;

// La ficha no ha llegado: red caída, tiempo agotado o error del servidor. No
// es lo mismo que un 404, que sí dice «no hay ficha».
const NO_LLEGO = Symbol("no-llego");

// Memoria de vista, no caché por tiempo: una entrada por artista, y se tira al
// navegar desde el `afterEach` del router. Sirve para no repetir la petición
// una vez por canción dentro de la misma pantalla.
//
// 🔴 **Aquí hubo un TTL de 300 s** con un comentario que decía «mismo TTL que el
// Cache-Control del servidor (max-age=300)». Las dos cosas eran falsas desde el
// 28.08.26: el servidor sirve `max-age=0, must-revalidate` con ETag, así que
// revalidar cuesta un **304 en 0 bytes** y no hacía falta caché por tiempo. El
// §4 lo cerró el 05.09.26.
const _fichas = new Map();
const _enVuelo = new Map();
let _indice = null;

/** Olvida las fichas leídas y obliga a volver a pedirlas. */
export function invalidar() {
  _fichas.clear();
  _enVuelo.clear();
  _indice = null;
}

export function normalizeArtistName(s = "") {
  return String(s)
    .toLowerCase()
    .replace(/\.(mp3|flac|wav|aac|ogg|m4a)$/i, "")
    .replace(/[_]+/g, " ")
    .replace(/[^\p{L}\p{N}\s]+/gu, " ") // fuera puntuación; conserva letras/números unicode
    .replace(/\s+/g, " ")
    .trim();
}

export function artistSlug(name) {
  return normalizeArtistName(name)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // sin diacríticos
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Baja la ficha de un artista. `null` cuando no está curado —el servidor
 * responde `404`—, y `NO_LLEGO` cuando falla la red o el servidor.
 *
 * ⚠️ `NO_LLEGO` **no se guarda**: el refresco del artista que suena vuelve a
 * pedirla, y en cuanto llegue manda la ficha.
 */
async function getFicha(slug) {
  if (_fichas.has(slug)) return _fichas.get(slug);
  if (_enVuelo.has(slug)) return _enVuelo.get(slug);

  // ⚠️ `fetch` a secas, sin `cache`: el navegador manda `If-None-Match` solo y
  // el servidor contesta 304. Forzarlo desde aquí ignoraría la política del
  // servidor, que se decide en un sitio. Ver `refrescoContenido.js`.
  const promesa = fetch(FICHA_URL(slug))
    .then((r) => {
      if (r.ok) return r.json();
      return r.status === 404 ? null : NO_LLEGO;
    })
    .catch(() => NO_LLEGO)
    .then((datos) => {
      _enVuelo.delete(slug);
      if (datos !== NO_LLEGO) _fichas.set(slug, datos);
      return datos;
    });

  _enVuelo.set(slug, promesa);
  return promesa;
}

// El índice de la biblioteca, solo para cuando la ficha no llega: dice si el
// artista tiene foto o bio curadas sin tener que salir fuera a buscarlas.
// Pesa 55 KB comprimido y se baja una vez por vista, y solo si hace falta.
async function getIndice() {
  if (_indice) return _indice;
  _indice = fetch(INDICE_URL)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null)
    .then((json) => {
      if (!json?.artists) {
        _indice = null;
        return null;
      }
      return new Map(json.artists.map((a) => [a.slug, a]));
    });
  return _indice;
}

// Devuelve la entrada editorial del artista o null si no hay dato
// (red caída, 404 o artista sin curar = null: seguir al siguiente fallback).
export async function getEditorialArtist(artistName) {
  if (!artistName) return null;

  const slug = artistSlug(artistName);
  const entry = await getFicha(slug);

  // La ficha existe pero no ha llegado. Si el índice dice que tiene foto o bio
  // curadas, no se sale a buscarlas fuera: la foto se toma del índice y la bio
  // espera a la ficha (`pendiente`). Si el índice tampoco llega, no sabemos
  // nada y la cadena sigue como sin ficha.
  if (entry === NO_LLEGO) {
    const enIndice = (await getIndice())?.get(slug);
    if (!enIndice || (!enIndice.hasBio && !enIndice.hasImage)) return null;
    return {
      pendiente: true,
      bio: null,
      bioSource: "",
      bioCredit: "",
      bioEnCamino: !!enIndice.hasBio,
      imageUrl:
        enIndice.hasImage && enIndice.image ? `${LISTEN}${enIndice.image}` : null,
      imageSource: "",
      imageCredit: "",
      musicbrainz: null,
      priority: false,
      links: { bandcamp: null, spotify: null, soundcloud: null, website: null, facebook: null },
    };
  }

  if (!entry) return null;

  return {
    bio: entry.bio?.text || null,
    bioSource: entry.bio?.source || "",
    bioCredit: entry.bio?.sourceName || "",

    // El género que cura la redacción. Hoy llega vacío en todas las fichas
    // miradas, pero el servidor está rellenándolo (16.09.26), y cuando llegue
    // manda sobre el de TheAudioDB como el resto de la ficha.
    genero: entry.genero || null,

    // ⚠️ **La imagen no tiene la misma forma que en el índice.** Allí era un
    // objeto —`image.path` relativo, con `source` y `sourceName` dentro— y aquí
    // `image` es ya **la URL absoluta y versionada**, con el crédito al lado en
    // dos campos sueltos. El servidor lo dejó así a propósito para no cambiar
    // la forma de `image`, que la app consume como URL. Así que aquí no se
    // prefija nada: prefijarla la rompería.
    imageUrl: entry.image || null,
    imageSource: entry.imageSource || "",
    imageCredit: entry.imageSourceName || "",
    // La apaisada (28.10.26, aditiva), con la misma forma que `image`: URL
    // absoluta y versionada, o `null`. Solo la usa el hero de Radio.
    imageWideUrl: entry.imageWide || null,
    imageWideSource: entry.imageWideSource || "",
    imageWideCredit: entry.imageWideSourceName || "",
    // §2.2 del contrato (09.09.26): lo que el servidor guarda de MusicBrainz.
    // **Puede no venir** —cubre 2.287 de los 2.633 artistas— y nunca llega
    // vacío: o está el bloque o no está.
    //
    // ⚠️ **`verificado: false` no es un formalismo.** Esos datos salen de una
    // búsqueda por nombre quedándose con el primer resultado, que es la misma
    // apuesta que hace esta web contra TheAudioDB: «Nirvana» devuelve cinco
    // artistas en MusicBrainz y siempre gana el de Cobain. Por eso el servidor
    // **no manda un `mbid`**: sería la misma apuesta con aspecto de llave dura.
    //
    // Lo que sí sirve es `desambiguacion`, que dice de quién se está hablando.
    // El día que exista el paso de confirmación en el studio, `verificado`
    // pasará a `true` y llegará el `mbid`; leerlo ya evita tener que tocar nada
    // entonces.
    musicbrainz: entry.musicbrainz || null,

    // §2.1 del contrato. Desde el 15.09.26 **ya no decide el orden** de foto y
    // bio, que manda siempre la ficha. Solo lo sigue leyendo la trayectoria:
    // con bio priorizada no se consulta TheAudioDB (ver `useNowPlaying`).
    priority: !!entry.priority,

    // §7 del contrato: los enlaces OFICIALES del artista, curados por la
    // redacción y por un barrido de MusicBrainz que solo toma relaciones
    // oficiales. Cada uno es una URL o `null`.
    //
    // ⚠️ El campo puede **no existir** en fichas antiguas, así que `undefined`
    // se trata como `null`. Y se devuelven tal cual, sin adornar: quien los
    // pinte decide el orden y el nombre.
    links: {
      bandcamp: entry.links?.bandcamp || null,
      spotify: entry.links?.spotify || null,
      soundcloud: entry.links?.soundcloud || null,
      website: entry.links?.website || null,
      facebook: entry.links?.facebook || null,
    },
  };
}
